/*
  Section assets loading on demand
*/
function loadSectionAssets(scope = document) {
  const sectionCssElements = [];
  const sectionJsElements = [];

  if (scope instanceof Element && scope.hasAttribute("data-section-css")) {
    sectionCssElements.push(scope);
  }

  if (scope instanceof Element && scope.hasAttribute("data-section-js")) {
    sectionJsElements.push(scope);
  }

  if (scope.querySelectorAll) {
    sectionCssElements.push(...scope.querySelectorAll("[data-section-css]"));
    sectionJsElements.push(...scope.querySelectorAll("[data-section-js]"));
  }

  sectionCssElements.forEach((el) => {
    const hrefs = el.dataset.sectionCss.split(",");

    hrefs.forEach((href) => {
      const trimmedHref = href.trim();

      if (!trimmedHref || document.querySelector(`link[href="${trimmedHref}"]`)) return;

      const link = document.createElement("link");
      link.href = trimmedHref;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    });
  });

  sectionJsElements.forEach((el) => {
    const srcs = el.dataset.sectionJs.split(",");

    srcs.forEach((src) => {
      const trimmedSrc = src.trim();

      if (!trimmedSrc || document.querySelector(`script[src="${trimmedSrc}"]`)) return;

      const script = document.createElement("script");
      script.src = trimmedSrc;
      script.defer = true;
      document.head.appendChild(script);
    });
  });
}

loadSectionAssets();

if ("MutationObserver" in window) {
  const sectionAssetsObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        loadSectionAssets(node);
      });
    });
  });

  sectionAssetsObserver.observe(document.body, { childList: true, subtree: true });
}

/*
  External HTTP links
*/
function decorateHttpsLinks() {
  document.querySelectorAll('a[href*="https://"]').forEach((link) => {
    if (link.closest(".header")) return;

    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
}

decorateHttpsLinks();

/*
  Color scheme switcher
*/
function initColorSchemeSwitcher() {
  const switcher = document.querySelector("[data-color-scheme-switcher]");
  const buttons = document.querySelectorAll("[data-color-scheme-option]");

  if (!switcher || !buttons.length) {
    return;
  }

  function setColorScheme(scheme) {
    document.documentElement.dataset.theme = scheme;
    localStorage.setItem("colorScheme", scheme);

    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.colorSchemeOption === scheme));
    });
  }

  const currentScheme = document.documentElement.dataset.theme || "light";

  setColorScheme(currentScheme);

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      setColorScheme(button.dataset.colorSchemeOption);
    });
  });
}

initColorSchemeSwitcher();

/*
  Fixed header
*/
let stickyHeaderCleanup;

function initStickyHeader() {
  if (stickyHeaderCleanup) {
    stickyHeaderCleanup();
  }

  const header = document.querySelector("#header-group .header");

  if (!header) {
    stickyHeaderCleanup = undefined;
    return;
  }

  const spacer = document.createElement("div");
  let headerTop = 0;
  let ticking = false;

  spacer.className = "header-sticky-spacer";
  spacer.setAttribute("aria-hidden", "true");
  header.after(spacer);

  function setFixed(isFixed) {
    header.classList.toggle("is-fixed", isFixed);
    spacer.classList.toggle("is-active", isFixed);
  }

  function getScrollY() {
    if (document.body.dataset.scrollLocked === "true") {
      const lockedScrollY = Number(document.body.dataset.scrollLockTop || 0);

      if (Number.isFinite(lockedScrollY)) {
        return lockedScrollY;
      }
    }

    return window.scrollY;
  }

  function update() {
    setFixed(getScrollY() >= headerTop);
    ticking = false;
  }

  function requestUpdate() {
    if (ticking) return;

    ticking = true;
    requestAnimationFrame(update);
  }

  function measure() {
    setFixed(false);
    headerTop = header.getBoundingClientRect().top + window.scrollY;
    spacer.style.height = `${header.offsetHeight}px`;
    document.documentElement.style.setProperty("--sticky-header-height", `${header.offsetHeight}px`);
    requestUpdate();
  }

  measure();

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", measure);
  window.addEventListener("load", measure);

  stickyHeaderCleanup = () => {
    window.removeEventListener("scroll", requestUpdate);
    window.removeEventListener("resize", measure);
    window.removeEventListener("load", measure);
    setFixed(false);
    document.documentElement.style.removeProperty("--sticky-header-height");
    spacer.remove();
    stickyHeaderCleanup = undefined;
  };
}

initStickyHeader();
window.addEventListener("shopify:section:load", initStickyHeader);

/*
  Media on view
*/
function initMediaOnView() {
  const mediaItems = Array.from(document.querySelectorAll("video, iframe[data-src]"));

  if (!mediaItems.length) {
    return;
  }

  function activateVideo(video) {
    if (video.dataset.poster && !video.poster) {
      video.poster = video.dataset.poster;
    }

    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });

    video.load();

    if (video.dataset.autoplay !== "false" && video.hasAttribute("data-autoplay")) {
      video.preload = "auto";
      video.play().catch(() => {
        // Ignore autoplay failures caused by browser policies.
      });
    }
  }

  function activateIframe(iframe) {
    if (iframe.dataset.src && !iframe.getAttribute("src")) {
      iframe.setAttribute("src", iframe.dataset.src);
    }
  }

  function activateMedia(media) {
    if (media.tagName === "VIDEO") {
      activateVideo(media);
    } else if (media.tagName === "IFRAME") {
      activateIframe(media);
    }
  }

  if (!("IntersectionObserver" in window)) {
    mediaItems.forEach(activateMedia);
    return;
  }

  const observer = new IntersectionObserver((entries, instance) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      activateMedia(entry.target);
      instance.unobserve(entry.target);
    });
  }, { rootMargin: "300px 0px" });

  mediaItems.forEach((media) => {
    observer.observe(media);
  });
}

initMediaOnView();

/*
  Viewport animations
*/
function initViewportAnimations() {
  const observedElements = new Set();
  const animationSelector = '[class*="scroll-animate-"]';
  const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const isDesignMode = document.documentElement.dataset.designMode === "true";

  function getViewportElements(scope = document) {
    const elements = [];

    if (scope instanceof Element && scope.matches("[data-viewport-entered]")) {
      elements.push(scope);
    }

    if (scope.querySelectorAll) {
      elements.push(...scope.querySelectorAll("[data-viewport-entered]"));
    }

    return elements;
  }

  function setChildAnimationIndexes(el) {
    const children = Array.from(el.children);
    const animationChildren = el.hasAttribute("data-animate-children")
      ? children
      : children.filter((child) => child.matches(animationSelector));

    animationChildren.forEach((child, index) => {
      child.style.setProperty("--animation-index", index);
    });
  }

  function enterViewport(el) {
    setChildAnimationIndexes(el);
    el.dataset.viewportEntered = "true";
    observedElements.delete(el);
  }

  if (isDesignMode || reduceMotionQuery.matches || !("IntersectionObserver" in window)) {
    function enterViewportElements(scope = document) {
      getViewportElements(scope).forEach((el) => {
        if (el.dataset.animateOnScroll === "false") return;

        enterViewport(el);
      });
    }

    enterViewportElements();

    if (isDesignMode && "MutationObserver" in window) {
      const designModeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            enterViewportElements(node);
          });
        });
      });

      designModeObserver.observe(document.body, { childList: true, subtree: true });
    }

    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      enterViewport(entry.target);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -60px 0px" });

  function observeViewportElements(scope = document) {
    getViewportElements(scope).forEach((el) => {
      if (observedElements.has(el)) return;
      if (el.dataset.animateOnScroll === "false") return;

      observedElements.add(el);
      observer.observe(el);
    });
  }

  observeViewportElements();

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        observeViewportElements(node);
      });
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

initViewportAnimations();

/*
  SVG inline utility
*/
async function inlineSvgImages() {
  const images = document.querySelectorAll("img[data-transform-svg-inline]");

  return Promise.all(
    Array.from(images).map(async (img) => {
      const source = img.currentSrc || img.getAttribute("src") || "";

      if (img.dataset.inlineSvgProcessed === "true" || !source.split("?")[0].toLowerCase().endsWith(".svg")) {
        return null;
      }

      try {
        const response = await fetch(source, { credentials: "same-origin" });
        if (!response.ok) {
          return null;
        }

        const markup = await response.text();
        const svgDocument = new DOMParser().parseFromString(markup, "image/svg+xml");
        const svg = svgDocument.querySelector("svg");

        if (!svg) {
          return null;
        }

        ["class", "height", "id", "style", "width"].forEach((attribute) => {
          const value = img.getAttribute(attribute);
          if (value) {
            svg.setAttribute(attribute, value);
          }
        });

        const alt = img.getAttribute("alt");

        if (alt) {
          svg.setAttribute("role", "img");
          svg.setAttribute("aria-label", alt);
        } else {
          svg.setAttribute("aria-hidden", "true");
        }

        img.dataset.inlineSvgProcessed = "true";
        img.replaceWith(svg);
        return svg;
      } catch (error) {
        console.error("Error inlining SVG:", error);
        return null;
      }
    })
  );
}

inlineSvgImages();
window.addEventListener("shopify:section:load", inlineSvgImages);

/*
  Shopify helpers
*/
window.Shopify = window.Shopify || {};

window.Shopify.formatMoney = function (cents, format) {
  const normalizedCents = typeof cents === "string" ? cents.replace(".", "") : cents;
  const defaultFormat = window.Shopify.money_format || "${{amount}}";
  const formatString = format || defaultFormat;

  const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    if (Number.isNaN(number) || number == null) return "0";

    const formattedNumber = (number / 100).toFixed(precision);
    const [integerPart, decimalPart] = formattedNumber.split(".");
    const formattedInt = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return decimalPart ? `${formattedInt}${decimal}${decimalPart}` : formattedInt;
  };

  const placeholderMatch = formatString.match(/{{\s*(\w+)\s*}}/);
  if (!placeholderMatch) return normalizedCents;

  const placeholder = placeholderMatch[1];
  let value;

  switch (placeholder) {
    case "amount":
      value = formatWithDelimiters(normalizedCents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(normalizedCents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(normalizedCents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(normalizedCents, 0, ".", ",");
      break;
    default:
      value = formatWithDelimiters(normalizedCents, 2);
  }

  return formatString.replace(placeholderMatch[0], value);
};

window.Shopify.resizeImage = function (src, size, crop = "") {
  if (!src) {
    return "data:image/svg+xml,%3Csvg class='placeholder-svg' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 525.5 525.5'%3E%3C/svg%3E";
  }

  return src
    .replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
      "."
    )
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
      const cropSuffix = crop.length ? `_crop_${crop}` : "";
      return `_${size}${cropSuffix}${match}`;
    });
};
